/**
 * 3 Col Masonry Gallery layout (bskr/layout-three-col-masonry-gallery).
 *
 * 1) Scroll-triggered staggered reveal of the gallery images (opacity 0→1 +
 *    slight slide-up).
 * 2) Truncation: only 6 images are shown — when the gallery holds more, the
 *    root gets `.is-truncated` so the CSS surfaces the bottom gradient + the
 *    "Ver Galería" CTA and hides the 7th+ images.
 * 3) Lightbox: the CTA opens a modal Swiper (1 slide/view) with prev/next
 *    arrows and thumbnail pagination (30×40 portrait). Slides are built from
 *    the core/gallery images on first open (so they load lazily).
 *
 * Loaded globally (layouts can't ship reliable per-block scripts). Skips the
 * editor; respects prefers-reduced-motion (images stay visible, no reveal).
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Swiper from "swiper";
import { Navigation, Pagination, Keyboard } from "swiper/modules";

gsap.registerPlugin(ScrollTrigger);

const MAX_VISIBLE = 6;

// --- Lightbox ---------------------------------------------------------------
function setupLightbox(root, images) {
  const cta = root.querySelector(".bskr-three-col-masonry-gallery__more .button");
  const modal = root.querySelector(".bskr-three-col-masonry-gallery__lightbox");
  if (!cta || !modal || !images.length) return;

  const wrapper = modal.querySelector(".swiper-wrapper");
  const swiperEl = modal.querySelector(".swiper");
  let swiper = null;
  let built = false;

  function build() {
    if (built) return;
    built = true;

    wrapper.innerHTML = images
      .map(
        (img) =>
          `<div class="swiper-slide"><img src="${img.src}" alt="${img.alt}" loading="lazy" decoding="async"></div>`
      )
      .join("");

    swiper = new Swiper(swiperEl, {
      modules: [Navigation, Pagination, Keyboard],
      slidesPerView: 1,
      spaceBetween: 16,
      loop: images.length > 1,
      keyboard: { enabled: true },
      navigation: {
        prevEl: modal.querySelector(".swiper-button-prev"),
        nextEl: modal.querySelector(".swiper-button-next"),
      },
      pagination: {
        el: modal.querySelector(".swiper-pagination"),
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className} bskr-three-col-masonry-gallery__lightbox-thumb"><img src="${images[index].src}" alt="" loading="lazy" width="30" height="40"></span>`,
      },
    });
  }

  function open() {
    build();
    modal.hidden = false;
    // next frame so the transition can play from the hidden state
    requestAnimationFrame(() => modal.classList.add("is-open"));
    document.body.classList.add("is-lightbox-open");
    window.lenisInstance?.stop?.();
    if (swiper) swiper.update();
  }

  function close() {
    modal.classList.remove("is-open");
    document.body.classList.remove("is-lightbox-open");
    window.lenisInstance?.start?.();
    // hide after the fade-out
    const onEnd = () => {
      modal.hidden = true;
      modal.removeEventListener("transitionend", onEnd);
    };
    modal.addEventListener("transitionend", onEnd);
  }

  cta.addEventListener("click", (e) => {
    e.preventDefault();
    open();
  });

  modal.querySelectorAll("[data-lightbox-close]").forEach((el) =>
    el.addEventListener("click", close)
  );

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal.classList.contains("is-open")) close();
  });
}

// --- Reveal + truncation ----------------------------------------------------
function initBlock(root) {
  if (root.dataset.masonryGalleryInited === "1") return;
  root.dataset.masonryGalleryInited = "1";

  const grid = root.querySelector(".bskr-three-col-masonry-gallery__grid");
  if (!grid) return;

  const figures = grid.querySelectorAll("figure.wp-block-image");
  if (!figures.length) return;

  // Collect image data (all of them) for the lightbox.
  const images = Array.prototype.map
    .call(figures, (fig) => {
      const img = fig.querySelector("img");
      return img ? { src: img.currentSrc || img.src, alt: img.alt || "" } : null;
    })
    .filter(Boolean);

  // Truncation is layout, not motion: apply it regardless of reduced-motion.
  if (figures.length > MAX_VISIBLE) {
    root.classList.add("is-truncated");
    setupLightbox(root, images);
  }

  // Reveal: skip for reduced-motion users (images stay visible). Truncation +
  // lightbox above still apply.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Inside a Content Slider slide the vertical scroll trigger can't fire (the
  // slide is translated horizontally); skip the reveal so the images keep their
  // default visible state (see content-slider.js / content-slide markup).
  if (root.closest("[data-no-scroll-reveal]")) return;

  // Flag the section so the CSS hides the images before GSAP takes over
  // (avoids a flash); only added here, so no-JS users keep them visible.
  root.classList.add("is-anim-ready");

  const visible = Array.prototype.slice.call(figures, 0, MAX_VISIBLE);
  gsap.set(visible, { opacity: 0, y: 24 });
  gsap.to(visible, {
    opacity: 1,
    y: 0,
    duration: 0.6,
    ease: "power2.out",
    stagger: 0.1,
    scrollTrigger: {
      trigger: root,
      start: "top 85%",
      once: true,
    },
  });
}

function init() {
  // Skip in the editor; leave reduced-motion users with static, visible images.
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;

  document
    .querySelectorAll(".wp-block-bskr-layout-three-col-masonry-gallery")
    .forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
